import { Component, ViewChild, ElementRef, AfterViewChecked, OnInit, OnDestroy, HostListener, ChangeDetectorRef, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MemoryStorageService } from './core/services/memory-storage.service';
import swal from 'sweetalert2';
import { SignalRService } from './core/services/signalr.service';
import { DestinationVideoService, DestinationConfig } from './core/services/destination-video.service';
import { LocationScene } from './core/models/destination-video-scenes';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewChecked, OnDestroy {
  hideFooter: boolean = false;
  hideHeader: boolean = false;
  isGlobeRoute: boolean = false;
  isSignUpRoute: boolean = false;
  private videoPlayAttempted: boolean = false;

  // Dynamic video background & caching state
  selectedDestination: DestinationConfig | null = null;
  videoBlobUrl: string | null = null;
  posterUrl: string = '';
  journeyBackgroundUrl: string = '';
  candidateSources: Array<{ url: string; type: string }> = [];
  isVideoLoaded: boolean = false;
  private currentLoadedKey: string = '';
  private isMobileView: boolean = false;

  // Location text overlay state
  currentLocationScene: LocationScene | null = null;
  isLocationTextVisible: boolean = false;
  private currentSceneIndex: number = -1;
  private locationAnimFrameId: any = null;

  @ViewChild('bgVideo') bgVideoRef: ElementRef<HTMLVideoElement>;

  constructor(
    private route: ActivatedRoute,
    private signalRService: SignalRService,
    private router: Router,
    private destinationVideoService: DestinationVideoService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    this.isMobileView = this.destinationVideoService.isMobileDevice();

    this.route.queryParams.subscribe((params) => {
      this.hideFooter = params['hidefooter'] === 'true';
      this.hideHeader = params['hideheader'] === 'true';
    });

    // Detect routes to switch layout and video
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.handleRouteChange(event.urlAfterRedirects);
    });

    // Initial route check
    this.handleRouteChange(this.router.url);

    // Reactively update video if destination changes in service (e.g. from globe or form input)
    this.destinationVideoService.destination$.subscribe((destId) => {
      if (this.isSignUpRoute && destId) {
        this.loadDestinationVideo(destId);
      }
      if (!this.isGlobeRoute && !this.isSignUpRoute) {
        this.updateJourneyBackground(destId);
      }
    });

    this.signalRService.sessionInvalid$.subscribe((message: string) => {
      if (message) {
        swal.fire(message);
        localStorage.clear();
        MemoryStorageService.clear();
        return this.router.navigateByUrl('/assist-journey/session-expired');
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    const isNowMobile = this.destinationVideoService.isMobileDevice();
    if (isNowMobile !== this.isMobileView) {
      this.isMobileView = isNowMobile;
      if (this.isSignUpRoute) {
        this.currentLoadedKey = '';
        this.loadDestinationVideo();
      } else if (!this.isGlobeRoute) {
        this.updateJourneyBackground();
      }
    }
  }

  private handleRouteChange(url: string) {
    const urlWithoutQuery = url.split('?')[0];
    const cleanUrl = urlWithoutQuery.length > 1 && urlWithoutQuery.endsWith('/') ? urlWithoutQuery.slice(0, -1) : urlWithoutQuery;
    this.isGlobeRoute = cleanUrl === '/' || cleanUrl === '' || cleanUrl === '/book-now-pay-later';
    this.isSignUpRoute = cleanUrl === '/assist-journey' || cleanUrl === '/assist-journey/sign-up';

    if (this.isSignUpRoute) {
      this.loadDestinationVideo();
      this.startLocationTracking();
      setTimeout(() => {
        const container = document.getElementById('signupPageContainer');
        if (container) {
          container.scrollTop = 0;
        }
      }, 50);
    } else {
      this.stopLocationTracking();
      this.currentLocationScene = null;
      this.isLocationTextVisible = false;
      this.currentSceneIndex = -1;
      this.videoPlayAttempted = false;
      this.isVideoLoaded = false;
      if (!this.isGlobeRoute) {
        this.updateJourneyBackground();
        window.scrollTo(0, 0);
      }
    }
  }

  private updateJourneyBackground(overrideDest?: string) {
    let destinationParam: string | null = overrideDest || null;
    if (!destinationParam) {
      try {
        const urlTree = this.router.parseUrl(this.router.url);
        destinationParam = urlTree.queryParams['d'] || urlTree.queryParams['dest'] || urlTree.queryParams['destination'] || urlTree.queryParams['destinationName'];
      } catch (e) {
        destinationParam = this.route.snapshot.queryParamMap.get('d') || this.route.snapshot.queryParamMap.get('dest') || this.route.snapshot.queryParamMap.get('destination');
      }
    }

    if (!destinationParam) {
      destinationParam = this.destinationVideoService.getSelectedDestination();
    }

    const variant = this.isMobileView ? 'mobile' : 'desktop';
    this.journeyBackgroundUrl = this.destinationVideoService.getBackgroundImageUrl(destinationParam, variant);
  }

  private async loadDestinationVideo(overrideDest?: string) {
    let destinationParam: string | null = overrideDest || null;
    if (!destinationParam) {
      try {
        const urlTree = this.router.parseUrl(this.router.url);
        destinationParam = urlTree.queryParams['d'] || urlTree.queryParams['dest'] || urlTree.queryParams['destination'] || urlTree.queryParams['destinationName'];
      } catch (e) {
        destinationParam = this.route.snapshot.queryParamMap.get('d') || this.route.snapshot.queryParamMap.get('dest') || this.route.snapshot.queryParamMap.get('destination');
      }
    }

    if (!destinationParam) {
      destinationParam = this.destinationVideoService.getSelectedDestination();
    }

    const { destination, variant, primaryUrl, posterUrl, sources } =
      this.destinationVideoService.getVideoSources(destinationParam);

    const loadKey = `${destination.id}_${variant}`;
    if (this.currentLoadedKey === loadKey && this.videoBlobUrl) {
      return;
    }

    this.currentLoadedKey = loadKey;
    this.selectedDestination = destination;
    this.posterUrl = posterUrl;
    this.candidateSources = sources;
    this.isVideoLoaded = false; // Show poster image of selected location until new video loads
    this.videoPlayAttempted = false;
    this.currentLocationScene = null;
    this.isLocationTextVisible = false;
    this.currentSceneIndex = -1;

    // Immediately stop and clear previous video so it does not continue playing
    this.videoBlobUrl = null;
    if (this.bgVideoRef?.nativeElement) {
      const video = this.bgVideoRef.nativeElement;
      try {
        video.pause();
        video.currentTime = 0;
        video.removeAttribute('src');
        video.load();
      } catch (e) {}
    }
    this.cdr.detectChanges();

    try {
      const blobUrl = await this.destinationVideoService.getCachedVideoBlobUrl(primaryUrl);
      this.videoBlobUrl = blobUrl;

      if (this.bgVideoRef?.nativeElement) {
        const video = this.bgVideoRef.nativeElement;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.src = blobUrl;
        video.load();
        video.play().catch(() => {});
      }
    } catch (err) {
      console.warn('Failed to load cached video blob, falling back to direct stream:', err);
      this.videoBlobUrl = null;
    }
  }

  onVideoCanPlay() {
    this.isVideoLoaded = true;
    this.updateLocationScene();
    if (this.bgVideoRef?.nativeElement) {
      const video = this.bgVideoRef.nativeElement;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.play().catch(() => {});
    }
  }

  onVideoTimeUpdate() {
    this.updateLocationScene();
  }

  ngAfterViewChecked() {
    if (this.isSignUpRoute && this.bgVideoRef?.nativeElement && !this.videoPlayAttempted) {
      this.videoPlayAttempted = true;
      const video = this.bgVideoRef.nativeElement;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.play().catch(() => {});
    }
    if (!this.isSignUpRoute) {
      this.videoPlayAttempted = false;
    }
  }

  scrollToSignupForm() {
    const el = document.getElementById('signupFormSection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  private startLocationTracking() {
    this.stopLocationTracking();
    if (typeof window === 'undefined') return;

    this.ngZone.runOutsideAngular(() => {
      const loop = () => {
        if (!this.isSignUpRoute) {
          this.locationAnimFrameId = null;
          return;
        }
        this.updateLocationScene();
        this.locationAnimFrameId = requestAnimationFrame(loop);
      };
      this.locationAnimFrameId = requestAnimationFrame(loop);
    });
  }

  private stopLocationTracking() {
    if (this.locationAnimFrameId) {
      cancelAnimationFrame(this.locationAnimFrameId);
      this.locationAnimFrameId = null;
    }
  }

  private updateLocationScene() {
    if (!this.isSignUpRoute || !this.bgVideoRef?.nativeElement) {
      if (this.currentLocationScene !== null || this.isLocationTextVisible) {
        this.currentLocationScene = null;
        this.isLocationTextVisible = false;
        this.currentSceneIndex = -1;
        this.cdr.detectChanges();
      }
      return;
    }

    const video = this.bgVideoRef.nativeElement;
    const currentTime = video.currentTime || 0;
    const duration = (video.duration && isFinite(video.duration) && video.duration > 0) ? video.duration : 20;

    const variant = this.isMobileView ? 'mobile' : 'desktop';
    const destId = this.selectedDestination ? this.selectedDestination.id : 'global';

    const { scene, sceneIndex, isVisible } = this.destinationVideoService.getLocationScene(
      destId,
      variant,
      currentTime,
      duration
    );

    let stateChanged = false;

    if (this.currentSceneIndex !== sceneIndex) {
      this.currentSceneIndex = sceneIndex;
      this.currentLocationScene = scene;
      stateChanged = true;
    }

    if (this.isLocationTextVisible !== isVisible) {
      this.isLocationTextVisible = isVisible;
      stateChanged = true;
    }

    if (stateChanged) {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.stopLocationTracking();
    window.removeEventListener('beforeunload', this.clearStorage);
  }

  private clearStorage() {
    if (environment.production) {
      MemoryStorageService.clear();
    }
  }
}
